/**
 * @fileoverview One walkthrough's own page: /academy/onboarding/:slug, the address to
 * share. `?t=` starts it at a moment, so a link can point straight at a step.
 * An unknown film goes back to the hub rather than to the home page, where
 * someone following an old link can still find what replaced it.
 */

import { Link, Navigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

import PublicLayout from "@/components/PublicLayout";
import WalkthroughPlayer from "@/components/WalkthroughPlayer";
import { WALKTHROUGH_PORTALS, walkthroughBySlug } from "@/lib/walkthroughs";

export default function AcademyWalkthroughPage() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const film = walkthroughBySlug(slug);
  if (!film) return <Navigate to="/academy/onboarding" replace />;

  const portal = WALKTHROUGH_PORTALS.find((p) => p.id === film.portal);
  const startAt = Math.max(0, Math.min(Number(params.get("t")) || 0, film.seconds - 1));

  return (
    <PublicLayout>
      <main className="relative z-10 mx-auto max-w-6xl px-4 pb-24 pt-10 md:px-8 md:pt-16 safe-area-padding">
        <Link
          to="/academy/onboarding"
          className="mb-8 inline-flex items-center gap-1.5 font-sans text-sm text-[var(--text-tertiary)] transition duration-200 hover:text-[var(--text-primary)]"
        >
          <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> Onboarding
        </Link>
        <header className="mb-8 max-w-3xl">
          <p className="mb-3 font-mono text-[11px] uppercase tracking-widest text-[var(--accent-yellow-text)]">
            {portal?.title ?? "Walkthrough"}
          </p>
          <h1 className="font-serif text-3xl font-light tracking-tight text-[var(--text-primary)] md:text-5xl">{film.title}</h1>
          <p className="mt-4 font-sans text-base text-[var(--text-secondary)]">{film.summary}</p>
        </header>
        <WalkthroughPlayer walkthrough={film} startAt={startAt} />
      </main>
    </PublicLayout>
  );
}
